import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import React from "react";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import MakePicks from "./pages/MakePicks";
import Leaderboard from "./pages/Leaderboard";
import AccountSettings from "./pages/AccountSettings";
import AdminPanel from "./pages/AdminPanel";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import LeagueManagement from "./pages/LeagueManagement";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Components
import Layout from "./components/Layout";
import LoadingScreen from "./components/LoadingScreen";

// Hooks
import { useAppHeight } from "./hooks/useAppHeight";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Protected route component for admin-only pages
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user?.isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

// Protected route component for commissioner-only pages
const CommissionerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user?.isCommissioner) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  // Publishes --app-height / --chrome-inset-bottom so full-height screens fit
  // the viewport actually left visible by the mobile browser's URL bar.
  useAppHeight();

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  return (
    <Router>
      <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="picks" element={<MakePicks />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="account" element={<AccountSettings />} />
            <Route
              path="league-management"
              element={
                <CommissionerRoute>
                  <LeagueManagement />
                </CommissionerRoute>
              }
            />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
            <Route
              path="admin/email-templates"
              element={
                <AdminRoute>
                  <AdminEmailTemplates />
                </AdminRoute>
              }
            />
          </Route>

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
    </Router>
  );
}

export default App;
